/* JSON-LD helpers for Organization, WebSite, BreadcrumbList, Product, Article,
 * FAQPage. Per APEX_DIGITAL_DESIGN_BRIEF.md §9 + Bible §12.2.
 */

import { socialSameAs } from "./social-channels";
import { SPIKER_VERIFIED_REVIEWS } from "./spiker-proof";
import { uploadDateISO } from "./videos";

const SITE =
  process.env.NEXT_PUBLIC_SITE_URL || "https://digital.apexflowlabs.com";

/* ONE Brian across every Apex Flow Labs property.
 *
 * Anchored to the PARENT domain on purpose. A Digital-scoped id
 * (digital.apexflowlabs.com/about/brian#person) mints a SEPARATE person on each
 * property, so the 13 years at Spiker Carpet & Tile Care count for nothing
 * here - which is the entire authority argument. Every AFL property must
 * reference THIS exact string. Same defect class as the @id-less store nodes
 * fixed on 2026-08-09, one level up the graph.
 *
 * THE FRAGMENT IS #founder, NOT #brian. This was measured, not chosen:
 * www.apexflowlabs.com already publishes
 *   {"@type":"Person","name":"Brian Spiker","@id":".../#founder"}
 * and its own Organization.founder points at that id. An earlier version of
 * this constant said #brian, which referenced NOTHING - a dangling reference
 * is the same defect as a dead sameAs: a claim pointing at empty space.
 * Do not rename this fragment unless the PARENT node is renamed first. */
export const PERSON_ID = "https://www.apexflowlabs.com/#founder";

export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "OnlineStore",
    /* Stable @id so every other node (ImageObject creator, Product brand, etc.)
     * can point at ONE canonical Organization entity instead of fragmenting. */
    "@id": `${SITE}/#organization`,
    name: "Apex Flow Digital",
    /* BRIAN RULED, 2026-08-09: no rename, no new domain, no migration.
     *
     * `name` stays the DIVISION name, because that is what the copy says. The
     * qualified form lives in `alternateName` and in every title tag, and it is
     * the only brand string we optimise for. We do NOT compete for the bare
     * two-word term: apexflowdigital.com is a live, unrelated SEO agency and
     * apexdigital.com is a live, unrelated IT company. Both were measured.
     *
     * The historical "Apex Digital" values were REMOVED from alternateName on
     * purpose. alternateName is what tells an engine "also known as", so
     * keeping it would have kept re-associating this entity with the very name
     * we just decided to stop competing for. Do not add it back to chase old
     * citations - that is the trade Brian made knowingly.
     *
     * The entity that ranks is Apex Flow Labs. Digital is a division of it. */
    alternateName: ["Apex Flow Labs Digital"],
    legalName: "Apex Flow Labs LLC",
    url: SITE,
    foundingDate: "2026",
    /* E-E-A-T provenance — the 13-year real-operator story as structured data,
     * not just visible copy, so answer engines can lift it. */
    description:
      `Apex Flow Digital is the digital-products arm of Apex Flow Labs, built by the operator of Spiker Carpet & Tile Care — a 13-year, owner-operated California service business (since 2013, ${SPIKER_VERIFIED_REVIEWS.toLocaleString()} verified reviews). We sell only what we use and can prove.`,
    /* E-E-A-T spine: the entity is a real operator, and this is who. Emitted by
     * REFERENCE so there is exactly one Person node in the graph. */
    founder: { "@type": "Person", "@id": PERSON_ID },
    parentOrganization: {
      "@type": "Organization",
      name: "Apex Flow Labs LLC",
      url: "https://www.apexflowlabs.com",
    },
    /* sameAs — ONLY Apex Flow Digital's OWN official profiles (its social
     * accounts). 2026-07-03 ENTITY FIX: previously this listed the parent
     * (apexflowlabs.com) + all 11 sister brands. That was wrong — schema.org
     * `sameAs` means "the SAME entity's other pages," so listing the parent +
     * siblings told Google + AI engines "Apex Flow Digital IS Apex Flow Labs IS
     * Apex Flow Books…" and collapsed the whole ecosystem into ONE entity (the
     * strongest node = the parent). Result: searches/AI for "Apex Flow Digital"
     * resolved to apexflowlabs.com, not the store. The ecosystem hierarchy is
     * ALREADY expressed correctly by `parentOrganization` above (that says
     * "child of," which does NOT merge entities). The store's own social
     * profiles populate this list from src/lib/social-channels.ts as the
     * accounts go live (empty = omitted, never a dead URL). */
    /* OMITTED entirely when we have no verified profiles - never `[]`.
     * Brian: "A dead sameAs is worse than none - it teaches every model our own
     * claims are unreliable." An empty array is a claim of no presence; absence
     * of the key is simply silence. Every URL is curl-checked for 200 before it
     * is allowed into social-channels.ts. */
    ...(socialSameAs().length ? { sameAs: socialSameAs() } : {}),
    logo: `${SITE}/apex-digital-logo.jpg`,
    /* Real business transparency — Google Merchant Center Misrepresentation
     * check wants a verifiable identity: phone, email, and locality. */
    telephone: "+1-916-720-3292",
    email: "info@apexflowlabs.com",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Galt",
      addressRegion: "CA",
      addressCountry: "US",
    },
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: "info@apexflowlabs.com",
        telephone: "+1-916-720-3292",
        areaServed: "US",
        availableLanguage: ["English"],
      },
    ],
  };
}

export function websiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE}/#website`,
    name: "Apex Flow Digital",
    /* The qualified form, matching organizationJsonLd. Never the bare
     * two-word term - see the ruling recorded there. */
    alternateName: ["Apex Flow Labs Digital"],
    url: SITE,
    /* Point at the canonical Organization @id rather than re-stating a name.
     * This previously read `{ name: "Apex Flow Labs" }`, which minted a second,
     * @id-less Organization AND named the PARENT as the site's publisher — the
     * site is published by the store, not by the holding company, and that
     * mis-statement is part of why the two entities kept collapsing into one.
     * The @id binds the WebSite to the single #organization node; the parent
     * relation is already carried correctly by that node's parentOrganization. */
    publisher: { "@id": `${SITE}/#organization` },
    /* SOP Phase 1.3 — SearchAction so Google can render the sitelinks
     * searchbox under the Apex Flow Digital brand result. Target points at
     * /find which hosts the niche finder; `search_term_string` is
     * Google's required placeholder for the buyer's query. */
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE}/find?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/* Hero Rebuild SOP Gate 2.2 — OnlineStore declares the departments + ties the
 * brand to commerce intent so the storefront ranks as a shop, not a blog.
 *
 * ENTITY FIX 2026-08-09 (measured, not theorised): the live homepage was
 * emitting THREE store-ish nodes — organizationJsonLd (@id #organization),
 * websiteJsonLd, and this one with NO @id at all. An @id-less node is a NEW
 * entity to a consumer, so the site was publishing a second, thinner, unlinked
 * "store" alongside its canonical one and splitting its own authority between
 * them. That is the same fragmentation the rename is meant to end, so fixing it
 * here matters as much as the name itself. Sharing the @id merges this node's
 * department/commerce signals INTO the canonical Organization instead of
 * competing with it. Keep this @id identical to organizationJsonLd's. */
export function onlineStoreJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "OnlineStore",
    "@id": `${SITE}/#organization`,
    name: "Apex Flow Digital",
    alternateName: ["Apex Flow Labs Digital"],
    url: SITE,
    parentOrganization: { "@type": "Organization", name: "Apex Flow Labs LLC" },
    department: [
      { "@type": "Thing", name: "Notion Systems" },
      { "@type": "Thing", name: "Canva Templates" },
      { "@type": "Thing", name: "Wall Art" },
      { "@type": "Thing", name: "3D Models" },
      { "@type": "Thing", name: "Printables" },
      { "@type": "Thing", name: "Business-in-a-Box Kits" },
    ],
  };
}

export function breadcrumbJsonLd(
  trail: Array<{ name: string; url: string }>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: t.name,
      item: t.url.startsWith("http") ? t.url : `${SITE}${t.url}`,
    })),
  };
}

export function productJsonLd(p: {
  name: string;
  description: string;
  image: string;
  priceCents: number;
  url: string;
  sku: string;
  /* GSC-2026-08-19: the date this offer began. Google asks for validFrom on
   * Merchant listings. It is NOT held at any current call site, so it is
   * optional and simply omitted when unknown - never guessed. Pass a real
   * publish date here as products start carrying one. */
  validFrom?: string;
}) {
  /* Guard against a $0.00 placeholder Offer (an unpriced cluster row would
   * otherwise serialize price "0.00" on an indexable Product — invalid to
   * Google Merchant). No price → emit the Product without an Offer. */
  const hasPrice = Number.isFinite(p.priceCents) && p.priceCents > 0;

  /* GSC-2026-08-19 reported "Invalid string length in field sku". Callers
   * pass whatever id or slug they hold, so an empty id or a very long slug
   * serialises an invalid sku. Omit it rather than emit a broken one: an
   * absent optional field is valid, an invalid one is an error. NOT
   * truncated - a truncated sku is a wrong sku, and sku is identity. */
  const skuRaw = (p.sku ?? "").trim();
  const validSku = skuRaw.length > 0 && skuRaw.length <= 50 ? skuRaw : null;
  const priceValidUntil = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.name,
    description: p.description,
    image: [p.image],
    ...(validSku ? { sku: validSku } : {}),
    brand: { "@type": "Brand", name: "Apex Flow Digital" },
    ...(hasPrice
      ? {
          offers: {
            "@type": "Offer",
            url: p.url,
            priceCurrency: "USD",
            price: (p.priceCents / 100).toFixed(2),
            priceValidUntil,
            ...(p.validFrom ? { validFrom: p.validFrom } : {}),
            availability: "https://schema.org/InStock",

            /* GSC-2026-08-19: Merchant listings wanted shippingDetails.
             * These are digital downloads - the refund policy contrasts
             * them with "the way a physical store can take back a returned
             * package" - so the rate is genuinely 0 and delivery is
             * immediate. A true declaration, not a placeholder. */
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingRate: {
                "@type": "MonetaryAmount",
                value: "0",
                currency: "USD",
              },
              shippingDestination: {
                "@type": "DefinedRegion",
                addressCountry: "US",
              },
              deliveryTime: {
                "@type": "ShippingDeliveryTime",
                handlingTime: {
                  "@type": "QuantitativeValue",
                  minValue: 0,
                  maxValue: 0,
                  unitCode: "DAY",
                },
                transitTime: {
                  "@type": "QuantitativeValue",
                  minValue: 0,
                  maxValue: 0,
                  unitCode: "DAY",
                },
              },
            },

            /* GSC-2026-08-19: /legal/refund-policy states a 14-day
             * make-it-right guarantee. It is CONDITIONAL - delivery
             * failures, wrong files, corrupted files - and schema.org
             * cannot express that condition, so merchantReturnLink carries
             * the real terms rather than overstating them here. */
            hasMerchantReturnPolicy: {
              "@type": "MerchantReturnPolicy",
              applicableCountry: "US",
              returnPolicyCategory:
                "https://schema.org/MerchantReturnFiniteReturnWindow",
              merchantReturnDays: 14,
              returnMethod: "https://schema.org/ReturnByMail",
              returnFees: "https://schema.org/FreeReturn",
              merchantReturnLink: `${SITE}/legal/refund-policy`,
            },
          },
        }
      : {}),
  };
}

/* ImageObject schema per SOP v10.0 §23.4 — surfaces the Licensable
 * Images badge in Google Image search by declaring an acquireLicensePage.
 * Use on every primary product image. */
export function licensedImageJsonLd(p: {
  contentUrl: string;
  name: string;
  caption?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    contentUrl: p.contentUrl,
    name: p.name,
    caption: p.caption ?? p.name,
    creator: {
      "@type": "Organization",
      "@id": `${SITE}/#organization`,
      name: "Apex Flow Digital",
    },
    creditText: "Apex Flow Digital",
    /* GSC-2026-08-19: Image metadata wanted copyrightNotice. Apex Flow Labs
     * LLC is the legal entity already declared as Organization.legalName
     * above - factual about our own images. */
    copyrightNotice: "\u00A9 Apex Flow Labs LLC",
    license: `${SITE}/legal/license`,
    acquireLicensePage: `${SITE}/legal/license`,
  };
}

/* Speakable schema per SOP v10.0 §18.7 — tells voice assistants
 * (Google Assistant, Siri, Alexa, Claude voice, ChatGPT voice) which
 * page regions to read aloud. cssSelector array works across all
 * assistants that support the spec. Embed on FAQ/description/how-to
 * pages. */
export function speakableJsonLd(cssSelectors: string[] = [".product-description", ".faq"]) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: cssSelectors,
    },
  };
}

export function articleJsonLd(a: {
  title: string;
  description: string;
  image: string;
  url: string;
  authorName: string;
  publishedAt: string;
  updatedAt: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: a.title,
    description: a.description,
    image: [a.image],
    mainEntityOfPage: a.url,
    datePublished: a.publishedAt,
    dateModified: a.updatedAt,
    author: { "@type": "Person", name: a.authorName },
    publisher: {
      "@type": "Organization",
      name: "Apex Flow Digital",
      logo: { "@type": "ImageObject", url: `${SITE}/apex-digital-logo.jpg` },
    },
  };
}

export function faqJsonLd(items: Array<{ q: string; a: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: it.a,
      },
    })),
  };
}

/* CollectionPage schema for hub / category routes per the
 * digital-frontend-build-sop.md SEO requirements. */
export function collectionPageJsonLd(p: {
  name: string;
  description: string;
  url: string;
  itemCount?: number;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: p.name,
    description: p.description,
    url: p.url.startsWith("http") ? p.url : `${SITE}${p.url}`,
    ...(p.itemCount
      ? {
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: p.itemCount,
          },
        }
      : {}),
  };
}

/* HowTo schema per SOP v10.0 §17.4 + §18.5. Use on every /how-to/*
 * page. Google surfaces these as featured snippets, voice assistants
 * read the step list aloud, and AI Overviews cite the steps verbatim. */
export function howToJsonLd(p: {
  name: string;
  description: string;
  totalTime?: string; // ISO 8601 e.g. PT15M
  supply?: string[];
  tool?: string[];
  steps: Array<{ name: string; text: string }>;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: p.name,
    description: p.description,
    ...(p.totalTime ? { totalTime: p.totalTime } : {}),
    ...(p.supply && p.supply.length
      ? {
          supply: p.supply.map((s) => ({
            "@type": "HowToSupply",
            name: s,
          })),
        }
      : {}),
    ...(p.tool && p.tool.length
      ? {
          tool: p.tool.map((t) => ({
            "@type": "HowToTool",
            name: t,
          })),
        }
      : {}),
    step: p.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };
}

/* Person schema for Brian Spiker — anchors E-E-A-T (Experience,
 * Expertise, Authoritativeness, Trustworthiness) per SOP v10.0 §21.
 * Embed on /about/brian and on any content page whose byline reads
 * "By Brian Spiker". `@id` stays stable across pages so the entity
 * resolves to a single person in the schema graph. */
export function brianPersonJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": PERSON_ID,
    name: "Brian Spiker",
    url: `${SITE}/about/brian`,
    jobTitle: "Founder",
    worksFor: [
      {
        "@type": "Organization",
        name: "Apex Flow Labs",
        url: "https://www.apexflowlabs.com",
      },
      {
        "@type": "LocalBusiness",
        name: "Spiker Carpet & Tile Care",
        url: "https://www.spikercarpetandtilecare.com",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Galt",
          addressRegion: "CA",
          addressCountry: "US",
        },
      },
    ],
    knowsAbout: [
      "Service business operations",
      "Small business systems",
      "Notion operating systems",
      "Digital product design",
      "Carpet and tile care",
      "Operator playbooks",
    ],
    sameAs: [
      "https://www.spikercarpetandtilecare.com",
      "https://www.apexflowlabs.com",
      "https://books.apexflowlabs.com",
      "https://www.apexflowlabs.com/academy",
    ],
  };
}

export function itemListJsonLd(items: Array<{ name: string; url: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      url: it.url.startsWith("http") ? it.url : `${SITE}${it.url}`,
    })),
  };
}

/* DefinedTerm schema per the Apex Glossary engine — definitional pages
 * LLMs and AI Overviews quote for "what is X" queries. Each glossary
 * term is marked as a schema.org/DefinedTerm belonging to the site-wide
 * glossary DefinedTermSet. */
export function definedTermJsonLd(t: {
  term: string;
  description: string;
  url: string;
  inDefinedTermSet?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    name: t.term,
    description: t.description,
    url: t.url.startsWith("http") ? t.url : `${SITE}${t.url}`,
    inDefinedTermSet: t.inDefinedTermSet ?? `${SITE}/glossary`,
  };
}

/* VideoObject — the schema that wins video rich results + AI citation. Every
 * SOP Step 7 video gets one (name/description/thumbnail/uploadDate/duration +
 * the on-page transcript). Feed it from the SITE_VIDEOS registry. */
export function videoObjectJsonLd(v: {
  name: string;
  description: string;
  thumbnailUrl: string | string[];
  uploadDate: string;
  duration?: string;
  contentUrl?: string;
  embedUrl?: string;
  transcript?: string;
  /** Chapters → Google "key moments" (hasPart/Clip). Needs watchUrl to deep-link. */
  chapters?: { start: number; name: string }[];
  watchUrl?: string;
  durationSeconds?: number;
}) {
  const chapters =
    v.chapters?.length && v.watchUrl
      ? v.chapters.map((c, i) => ({
          "@type": "Clip",
          name: c.name,
          startOffset: c.start,
          endOffset: v.chapters?.[i + 1]?.start ?? v.durationSeconds ?? c.start,
          url: `${v.watchUrl}&t=${c.start}s`,
        }))
      : undefined;
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: v.name,
    description: v.description,
    thumbnailUrl: v.thumbnailUrl,
    uploadDate: uploadDateISO(v.uploadDate),
    ...(v.duration ? { duration: v.duration } : {}),
    ...(v.contentUrl ? { contentUrl: v.contentUrl } : {}),
    ...(v.embedUrl ? { embedUrl: v.embedUrl } : {}),
    ...(v.transcript ? { transcript: v.transcript } : {}),
    ...(chapters ? { hasPart: chapters } : {}),
    publisher: {
      "@type": "Organization",
      name: "Apex Flow Digital",
      logo: { "@type": "ImageObject", url: `${SITE}/apex-digital-logo.jpg` },
    },
  };
}

/* SEC-P0 — ported verbatim from the Spiker fix (rainbo-webedit-repos/spiker,
 * lib/schema.ts `graph()`, shipped 2026-08-08) rather than rewritten, because a
 * reimplementation of this is exactly the kind of thing that looks right and
 * isn't. JSON.stringify does NOT escape '/', so any schema string containing
 * '</script>' closes the <script type="application/ld+json"> tag it is embedded
 * in and everything after it executes as HTML on the page. Escaping <, > and &
 * as their \uXXXX forms is the standard fix (same technique as Rails'
 * json_escape and the serialize-javascript package): the JSON parses back
 * byte-for-byte identical, it just can never spell out a literal tag.
 *
 * On this site the risk is HIGHER than it was on Spiker: JsonLdSchema.tsx feeds
 * this pattern schema fetched at runtime from a remote backend, so the input is
 * not even repo-controlled.
 *
 * THE TRAP (cost real time on Spiker, documented here so it isn't rediscovered):
 * the replacement strings must be DOUBLE-backslashed. In JS source '<' IS
 * the character '<', so replacing '<' with it is a perfect no-op that reads
 * exactly like a fix. '\\u003c' is the six characters the JSON text needs. The
 * self-test asserts on the OUTPUT string, not on the source, for this reason. */
export function jsonLdHtml(data: unknown): string {
  return JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026");
}

export function JsonLd({ data }: { data: object }) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonLdHtml(data) }}
    />
  );
}
